import { Check } from 'lucide-react';
import type { StepConfig } from '../../types/onboarding';

interface StepIndicatorProps {
  steps: StepConfig[];
  currentStep: number;
  /** The highest step the user has visited (allows forward navigation to visited steps) */
  highestStepVisited?: number;
  onStepClick: (step: number) => void;
}

export function StepIndicator({
  steps,
  currentStep,
  highestStepVisited,
  onStepClick,
}: StepIndicatorProps) {
  // Use highestStepVisited if provided, otherwise fall back to currentStep
  const maxClickableStep = highestStepVisited ?? currentStep;

  return (
    <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
      <div className="flex items-center justify-between min-w-max">
        {steps.map((step, index) => {
        const isCompleted = step.number < currentStep;
        const isCurrent = step.number === currentStep;
        // Allow clicking any step up to the highest visited
        const isClickable = step.number <= maxClickableStep;
        // A step is "visited" if it's been reached before but not currently active
        const isVisited = step.number <= maxClickableStep && step.number !== currentStep;

        return (
          <div key={step.number} className="flex items-center flex-1">
            {/* Step circle */}
            <button
              onClick={() => isClickable && onStepClick(step.number)}
              disabled={!isClickable}
              className={`
                relative flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all
                ${
                  isCompleted
                    ? 'bg-primary-600 text-white cursor-pointer hover:bg-primary-700'
                    : isCurrent
                      ? 'bg-primary-600 text-white ring-4 ring-primary-100'
                      : isVisited
                        ? 'bg-primary-200 text-primary-700 cursor-pointer hover:bg-primary-300'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }
              `}
              title={step.title}
            >
              {isCompleted ? (
                <Check className="w-4 h-4" />
              ) : (
                <span>{step.number}</span>
              )}
            </button>

            {/* Connector line */}
            {index < steps.length - 1 && (
              <div
                className={`
                  flex-1 h-1 mx-2 rounded
                  ${isCompleted ? 'bg-primary-600' : isVisited ? 'bg-primary-200' : 'bg-gray-200'}
                `}
              />
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
}
